import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Calculator, DollarSign, Percent, Calendar, CheckCircle } from 'lucide-react';

interface EmbeddableCalculatorProps {
  primaryColor?: string;
  companyName?: string;
  logoUrl?: string;
  leadCaptureEnabled?: boolean;
  onLeadSubmit?: (data: LeadData, calculationData: CalculationData) => Promise<void>;
}

interface LeadData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

interface CalculationData {
  homePrice: number;
  downPayment: number;
  downPaymentPercent: number;
  loanAmount: number;
  interestRate: number;
  loanTerm: number;
  monthlyPayment: number;
  propertyTax: number;
  insurance: number;
  totalMonthlyPayment: number;
}

export const EmbeddableCalculator: React.FC<EmbeddableCalculatorProps> = ({
  primaryColor = '#2563eb',
  companyName = 'EquiForge',
  logoUrl,
  leadCaptureEnabled = true,
  onLeadSubmit
}) => {
  const [homePrice, setHomePrice] = useState(400000);
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTerm, setLoanTerm] = useState(30);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const [leadData, setLeadData] = useState<LeadData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  // Calculations
  const downPayment = Math.round(homePrice * (downPaymentPercent / 100));
  const loanAmount = homePrice - downPayment;
  const monthlyRate = interestRate / 100 / 12;
  const numPayments = loanTerm * 12;
  
  const monthlyPrincipalInterest = monthlyRate > 0
    ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
      (Math.pow(1 + monthlyRate, numPayments) - 1)
    : loanAmount / numPayments;
  
  const propertyTax = Math.round((homePrice * 0.0125) / 12);
  const insurance = Math.round((homePrice * 0.005) / 12);
  const pmi = downPaymentPercent < 20 ? Math.round(loanAmount * 0.005 / 12) : 0;
  
  const totalMonthlyPayment = Math.round(monthlyPrincipalInterest + propertyTax + insurance + pmi);

  const calculationData: CalculationData = {
    homePrice,
    downPayment,
    downPaymentPercent,
    loanAmount,
    interestRate,
    loanTerm,
    monthlyPayment: Math.round(monthlyPrincipalInterest),
    propertyTax,
    insurance,
    totalMonthlyPayment
  };

  const handleGetResults = () => {
    if (leadCaptureEnabled) {
      setShowLeadCapture(true);
    }
  };

  const handleLeadSubmit = async () => {
    if (!leadData.email) return;
    
    setIsSubmitting(true);
    try {
      await onLeadSubmit?.(leadData, calculationData);
      setIsSuccess(true);
    } catch (error) {
      console.error('Error submitting lead:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (isSuccess) {
    return (
      <Card className="w-full max-w-lg mx-auto">
        <CardContent className="py-12 text-center">
          <div 
            className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <CheckCircle className="w-8 h-8" style={{ color: primaryColor }} />
          </div>
          <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
          <p className="text-muted-foreground mb-4">
            We've received your information. A {companyName} representative will contact you soon.
          </p>
          <div className="bg-muted/50 rounded-lg p-4 text-left">
            <p className="text-sm text-muted-foreground mb-1">Your Estimated Payment</p>
            <p className="text-3xl font-bold" style={{ color: primaryColor }}>
              {formatCurrency(totalMonthlyPayment)}/mo
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (showLeadCapture) {
    return (
      <Card className="w-full max-w-lg mx-auto">
        <CardHeader className="text-center pb-2">
          {logoUrl && (
            <img src={logoUrl} alt={companyName} className="h-10 mx-auto mb-2 object-contain" />
          )}
          <CardTitle className="text-lg">Get Your Personalized Results</CardTitle>
          <p className="text-sm text-muted-foreground">
            Enter your info to receive your calculation details
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground">Estimated Monthly Payment</p>
            <p className="text-3xl font-bold" style={{ color: primaryColor }}>
              {formatCurrency(totalMonthlyPayment)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatCurrency(homePrice)} home with {downPaymentPercent}% down
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="widget-firstName" className="text-xs">First Name</Label>
              <Input
                id="widget-firstName"
                value={leadData.firstName}
                onChange={(e) => setLeadData(prev => ({ ...prev, firstName: e.target.value }))}
                placeholder="John"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="widget-lastName" className="text-xs">Last Name</Label>
              <Input
                id="widget-lastName"
                value={leadData.lastName}
                onChange={(e) => setLeadData(prev => ({ ...prev, lastName: e.target.value }))}
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="widget-email" className="text-xs">Email *</Label>
            <Input
              id="widget-email"
              type="email"
              value={leadData.email}
              onChange={(e) => setLeadData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="john@example.com"
              required
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="widget-phone" className="text-xs">Phone (optional)</Label>
            <Input
              id="widget-phone"
              type="tel"
              value={leadData.phone}
              onChange={(e) => setLeadData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="(555) 123-4567"
            />
          </div>

          <Button
            onClick={handleLeadSubmit}
            disabled={isSubmitting || !leadData.email}
            className="w-full"
            style={{ backgroundColor: primaryColor }}
          >
            {isSubmitting ? 'Submitting...' : 'Get My Results'}
          </Button>

          <button
            onClick={() => setShowLeadCapture(false)}
            className="w-full text-sm text-muted-foreground hover:text-foreground"
          >
            ← Back to Calculator
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-lg mx-auto">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          {logoUrl ? (
            <img src={logoUrl} alt={companyName} className="h-8 object-contain" />
          ) : (
            <div className="flex items-center gap-2">
              <Calculator className="w-5 h-5" style={{ color: primaryColor }} />
              <span className="font-semibold">{companyName}</span>
            </div>
          )}
        </div>
        <CardTitle className="text-lg mt-2">Mortgage Calculator</CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-5">
        {/* Home Price */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2 text-sm">
              <DollarSign className="w-4 h-4" style={{ color: primaryColor }} />
              Home Price
            </Label>
            <span className="font-semibold">{formatCurrency(homePrice)}</span>
          </div>
          <Slider
            value={[homePrice]}
            onValueChange={([v]) => setHomePrice(v)}
            min={100000}
            max={2000000}
            step={10000}
            className="py-2"
          />
        </div>

        {/* Down Payment */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2 text-sm">
              <Percent className="w-4 h-4" style={{ color: primaryColor }} />
              Down Payment
            </Label>
            <span className="font-semibold">{downPaymentPercent}% ({formatCurrency(downPayment)})</span>
          </div>
          <Slider
            value={[downPaymentPercent]}
            onValueChange={([v]) => setDownPaymentPercent(v)}
            min={0}
            max={50}
            step={1}
            className="py-2"
          />
        </div>

        {/* Interest Rate */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2 text-sm">
              <Percent className="w-4 h-4" style={{ color: primaryColor }} />
              Interest Rate
            </Label>
            <span className="font-semibold">{interestRate}%</span>
          </div>
          <Slider
            value={[interestRate]}
            onValueChange={([v]) => setInterestRate(v)}
            min={2}
            max={12}
            step={0.125}
            className="py-2"
          />
        </div>

        {/* Loan Term */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4" style={{ color: primaryColor }} />
              Loan Term
            </Label>
            <span className="font-semibold">{loanTerm} years</span>
          </div>
          <div className="flex gap-2">
            {[15, 20, 30].map((term) => (
              <Button
                key={term}
                variant={loanTerm === term ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLoanTerm(term)}
                className="flex-1"
                style={loanTerm === term ? { backgroundColor: primaryColor } : {}}
              >
                {term} yr
              </Button>
            ))}
          </div>
        </div>

        {/* Results */}
        <div 
          className="rounded-lg p-4 text-center"
          style={{ backgroundColor: `${primaryColor}10` }}
        >
          <p className="text-sm text-muted-foreground mb-1">Estimated Monthly Payment</p>
          <p className="text-4xl font-bold" style={{ color: primaryColor }}>
            {formatCurrency(totalMonthlyPayment)}
          </p>
          <div className="grid grid-cols-3 gap-2 mt-3 text-xs text-muted-foreground">
            <div>
              <p>Principal & Interest</p>
              <p className="font-medium text-foreground">{formatCurrency(Math.round(monthlyPrincipalInterest))}</p>
            </div>
            <div>
              <p>Property Tax</p>
              <p className="font-medium text-foreground">{formatCurrency(propertyTax)}</p>
            </div>
            <div>
              <p>Insurance{pmi > 0 ? ' + PMI' : ''}</p>
              <p className="font-medium text-foreground">{formatCurrency(insurance + pmi)}</p>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        {leadCaptureEnabled && (
          <Button
            onClick={handleGetResults}
            className="w-full"
            size="lg"
            style={{ backgroundColor: primaryColor }}
          >
            Get Personalized Quote
          </Button>
        )}

        {/* Powered By */}
        <p className="text-xs text-center text-muted-foreground">
          Powered by <a href="https://equiforge.ai" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: primaryColor }}>EquiForge</a>
        </p>
      </CardContent>
    </Card>
  );
};
